import deepEqual from "deep-equal";
import invariant from "invariant";
import React from "react";
import APIEndpoint from "./endpoint";
import { useDispatch, useSelector } from "react-redux";

const ensureID = (id) => {
	if (typeof id === 'number') {
		id = String(id);
	}
	invariant((id === null || typeof id === 'string'), `ID must be a string or null but is ${id}`);
	return id;
};

const useEffectDeepCompare = (effect, deps) => {
	// Save the last parameters to detect changes
	const last = React.useRef(null);
	// If the deps have not changed with a deepCompare, keep the old reference
	// and React.useEffect won't detect the change
	deps = deepEqual(last.current, deps) ? last.current : deps;
	// overwrite the last parameters
	last.current = deps;

	React.useEffect(effect, deps);
};

// Returns a function that takes a state, and will return the default value if
// the endpoint is loading, or forward to the selector else
//
// ```
// const selector = defaultLoading(organizations, organizations.getTotalFromState, null);
// selector({ [organizations.index]: { loading: true }  }) === null
// selector({ [organizations.index]: { total: 10 }  }) === 10
// ```
const defaultLoading = (endpoint, selector, defaultValue) => state => {
	return endpoint.getLoadingFromState(state) ? defaultValue : selector(state);
};

/** get the count of values from the endpoint
* `endpoint` is an instance of APIEndpoint, `endpointOptions` is the options passed
* to `.list()`. The `limit: 0` is forced.
*
* options
*	- loading: the value used when the count is loading
* */
export function useCount(endpoint, endpointOptions, options={}) {
	invariant(endpoint instanceof APIEndpoint, 'useCount must be called with an APIEndpoint');

	const dispatch = useDispatch();

	// On mount, fetch the list, but only for the count
	useEffectDeepCompare(() => {
		dispatch(endpoint.list({
			...endpointOptions,
			limit: 0,
		}));
	}, [endpoint, endpointOptions]);

	const selector = ('loading' in options
		? defaultLoading(endpoint, endpoint.getTotalFromState, options.loading)
		: endpoint.getTotalFromState);
	return useSelector(selector);
}
/** get the count of values from the endpoint
* `endpoint` is an instance of APIEndpoint, `endpointOptions` is the options passed
* to `.count()`. The `limit: 0` is forced.
*
* options
*	- loading: the value used when the count is loading
* */
export function useTotalCount(endpoint, endpointOptions, options={}) {
	invariant(endpoint instanceof APIEndpoint, 'useCount must be called with an APIEndpoint');

	const dispatch = useDispatch();

	// On mount, fetch the list, but only for the count
	useEffectDeepCompare(() => {

		// If filter is expected but not yet available, skip
		if ('filter' in options && !endpointOptions.parameters?.[options.filter]) {
			return;
		}
		dispatch(endpoint.count({
			...endpointOptions,
			limit: 0,
		}));
	}, [endpoint, endpointOptions]);

	const selector = ('loading' in options
		? defaultLoading(endpoint, endpoint.getTotalFromState, options.loading)
		: endpoint.getTotalFromState);
	return useSelector(selector);
}

export function useTotal(endpoint) {
	invariant(endpoint instanceof APIEndpoint, 'useTotal must be called with an APIEndpoint');

	const selector = endpoint.getTotalFromState;
	return useSelector(selector);
}

/** get a list of instances by their id
 */
export function useInstances(endpoint, ids, options={}) {
	const dispatch = useDispatch();
	// Fetch the objects by their id
	useEffectDeepCompare(() => {
		dispatch(endpoint.retrieveMany(ids, options));
	}, [endpoint, ids]);

	return useSelector(state => endpoint.getManyFromState(state, ids));
}

/** get the values from the endpoint filtered by the query */
export function useValuesQuery(endpoint, query, endpointOptions) {
	invariant(endpoint instanceof APIEndpoint, 'useValues must be called with an APIEndpoint');

	const dispatch = useDispatch();

	useEffectDeepCompare(() => {
		dispatch(endpoint.setQuery(query));

		if (query) {
			dispatch(endpoint.list(endpointOptions));
		}
	}, [endpoint, query, endpointOptions]);

	return useSelector(endpoint.getValuesFromState);
}

/** get the list of values from the endpoint */
export function useValues(endpoint, endpointOptions) {
	invariant(endpoint instanceof APIEndpoint, 'useValues must be called with an APIEndpoint');

	const dispatch = useDispatch();

	// On mount, fetch the list
	useEffectDeepCompare(() => {
		dispatch(endpoint.list(endpointOptions));
	}, [endpoint, endpointOptions]);

	return useSelector(endpoint.getValuesFromState);
};

export function useValuesIterator(endpoint, endpointOptions) {
	const values = useValues(endpoint, endpointOptions);
	return {
		values,
		hasMore: useSelector(endpoint.getHasNextFromState),
		fetchNextPage: endpoint.useFetchNextPage(),
		loading: useSelector(endpoint.getLoadingFromState),
	};
}

/** get an instance from the endpoint */
export function useInstance(endpoint, id, endpointOptions) {
	invariant(endpoint instanceof APIEndpoint, 'useValues must be called with an APIEndpoint');
	id = ensureID(id);
	const dispatch = useDispatch();

	const { update, refresh } = endpointOptions || {};

	const instance = endpoint.useInstanceByID(id);
	// retrieve the instance
	React.useEffect(() => {
		if (id !== null && (instance === null || update)) {
			dispatch(endpoint.retrieve(id, endpointOptions));
		}
	// eslint-disable-next-line
	}, [endpoint, id, refresh]);

	return instance;
};

/** An update function */
export function useInstanceUpdater(endpoint, id, endpointOptions) {
	invariant(endpoint instanceof APIEndpoint, 'useValues must be called with an APIEndpoint');
	id = ensureID(id);

	const _updateFN = endpoint.useUpdate();
	const updateFN = React.useCallback(values => _updateFN(id, values, endpointOptions), [id, _updateFN, endpointOptions]);

	return updateFN;
}

/** An update function */
export function useInstanceCreator(endpoint, endpointOptions) {
	invariant(endpoint instanceof APIEndpoint, 'useValues must be called with an APIEndpoint');

	const _createFN = endpoint.useCreate();
	const createFN = React.useCallback(values => _createFN(values, endpointOptions), [_createFN, endpointOptions]);

	return createFN;
}

/** Return a clone of the endpoint with a lifetime matching the one of the
* component invoking the clone */
export function useClone(endpoint) {
	invariant(endpoint instanceof APIEndpoint, 'useClone must be called with an APIEndpoint');
	const dispatch = useDispatch();
	// Reference a cloned endpoint
	const ref = React.useRef(null);
	// Create the clone once
	if (ref.current === null) {
		ref.current = endpoint.clone();
	}
	// On unmount, dispose of the endpoint data
	React.useEffect(() => {
		return () => dispatch(ref.current.reset());
	// eslint-disable-next-line
	}, []);
	// Return the copy of the endpoint
	return ref.current;
}


export function useClones(endpoints) {
	invariant(endpoints.every(endpoint => endpoint instanceof APIEndpoint), 'useClone must be called with an APIEndpoint');	
	const dispatch = useDispatch();
	// Reference a cloned endpoint
	const clones = React.useRef([]);
	const lastCall = React.useRef([]);
	const oldClones = clones.current;
	// Create the clones once
	if (!deepEqual(lastCall.current, endpoints)) {
		clones.current = endpoints.map(endpoint => endpoint.clone());
		lastCall.current = endpoints;
	}
	// On unmount, dispose of the endpoint data
	React.useEffect(() => {
		return () => oldClones.forEach(clone => dispatch(clone.current.reset()));
	// eslint-disable-next-line
	}, [clones.current]);
	// Return the copy of the endpoint
	return clones.current;
}
